/**
 * Tracking Plan https://dsu-confluence.nestle.biz/display/DIANA/Hero+Machine+Product+Detail+Page+%28PDP%29+Tracking
 */
import { hasSubscriptions, type Product } from '@/domain/Product';
import {
  type Subscription,
  getListOfSubscriptionsDurations
} from '@/domain/Subscription';
import dispatchTrackingEvent, {
  productToGtmProductPayload,
  subscriptionToGtmSubscriptionPayload,
  subscriptionToGtmSubscriptionExtrasPayload,
  type PlansItem,
  productToGtmProductPayloadEvent,
  subscriptionToGtmSubscriptionPayloadEvent,
  productToGtmItemListPayload
} from '@/widgets/services/TrackingService.ts';
import type { Options } from './config';
import { generateEcommerceObject } from '@/utils/generateEcommerceObject';
import { PRICE_REPRESENTATION_MULTIPLIER } from '@/domain/Pricing';

const RAISED_BY = 'pdp_hero';

export function trackProductView(
  product: Product,
  currency: string,
  isDiscoveryOffer?: boolean
) {
  const subscriptionProductIncluded = hasSubscriptions(product);
  const value = product.pricing.price;
  const ecommerceObject = generateEcommerceObject(
    product,
    Boolean(isDiscoveryOffer),
    undefined
  );

  dispatchTrackingEvent({
    event: 'view_item',
    event_raised_by: RAISED_BY,
    subscription_product_included: subscriptionProductIncluded
      ? 'true'
      : 'false',
    ecommerce: {
      currency,
      value,
      items: [ecommerceObject!]
    }
  });
}

export function trackPageRanges(
  ranges: {
    title: string;
  }[] = []
): void {
  if (ranges.length === 0) return;

  const names = ranges.map((range) => range.title.toLowerCase());

  trackPdpInteraction(`tiers config - ${names.length}: ${names.join('|')}`);
}

export function trackPdpInteraction(action: string) {
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: action,
    event_raised_by: RAISED_BY
  });
}

export function trackViewItemList(
  products: Product[],
  currency: string,
  itemListName: string,
  itemListId?: string
) {
  dispatchTrackingEvent({
    event: 'view_item_list',
    event_raised_by: RAISED_BY,
    subscription_product_included: 'false',
    discovery_offer_included: 'false',
    ecommerce: {
      currency,
      value:
        products.reduce((total, p) => total + p.pricing.price, 0) /
        PRICE_REPRESENTATION_MULTIPLIER,
      items: products.map((product, idx) =>
        productToGtmItemListPayload(
          product,
          false,
          idx + 1,
          itemListId ?? `${RAISED_BY} - ${itemListName}`,
          itemListName
        )
      )
    }
  });
}

export function trackPageBuyOptions(
  buyOptionPlacement: Options['buyOptionsPlacement'],
  product: Product
): void {
  const options = buyOptionPlacement
    .map((buyOptionType) => buyOptionTypeToActionType[buyOptionType]?.(product))
    .filter((option) => option !== '');

  const actionTypeValue = options.length
    ? `sku | ${options.join(' | ')}`
    : 'sku';

  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: `buy options: ${actionTypeValue}`,
    event_raised_by: RAISED_BY
  });
}

export function trackAddToCartSubscription(
  product: Product,
  subscription: Subscription,
  subscriptionIndex: number,
  currency: string,
  isDiscoveryOffer: boolean
): void {
  dispatchTrackingEvent({
    event: 'add_to_cart',
    action_type: 'subscription',
    event_raised_by: RAISED_BY,
    click_location: 'pdp hero - subscription',
    checkout_type: undefined,
    subscription_product_included: 'true',
    ecommerce: {
      currency,
      value: subscription.parameters.periodicFee,
      items: [
        {
          ...productToGtmProductPayload(product, isDiscoveryOffer, true),
          ...subscriptionToGtmSubscriptionPayload(subscription),
          ...subscriptionToGtmSubscriptionExtrasPayload(
            product,
            subscription,
            subscriptionIndex
          )
        }
      ]
    }
  });
}

export function trackAboutSubscriptionsModalOpened(
  product: Product,
  subscription?: Subscription
) {
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: 'subscription info show',
    event_raised_by: RAISED_BY,
    ...productToGtmProductPayloadEvent(product),
    ...(subscription
      ? subscriptionToGtmSubscriptionPayloadEvent(subscription)
      : {})
  });
}

export function trackAboutSubscriptionsModalInteraction(
  elementLabel: {
    key: string;
    value: string;
  },
  product: Product,
  subscription?: Subscription
) {
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: 'subscription info click',
    event_raised_by: RAISED_BY,
    cta_name: `${elementLabel.key}: ${elementLabel.value} text clicked`,
    ...productToGtmProductPayloadEvent(product),
    ...(subscription
      ? subscriptionToGtmSubscriptionPayloadEvent(subscription)
      : {})
  });
}

export function trackModalOpened(
  type:
    | 'sku promo info show'
    | 'bundle promo info show'
    | 'subscription promo info show',
  product: Product,
  subscription?: Subscription
) {
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: type,
    event_raised_by: RAISED_BY,
    ...productToGtmProductPayloadEvent(product),
    ...(subscription
      ? subscriptionToGtmSubscriptionPayloadEvent(subscription)
      : {})
  });
}

export function trackPromotionsModalOpened(
  type: 'sku promo info show' | 'bundle info show' | 'subscription info show',
  product: Product,
  subscription?: Subscription
) {
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: type,
    event_raised_by: RAISED_BY,
    ...productToGtmProductPayloadEvent(product),
    ...(subscription
      ? subscriptionToGtmSubscriptionPayloadEvent(subscription)
      : {})
  });
}

export function trackPromotionsModalInteraction(
  type:
    | 'sku promo info click'
    | 'bundle promo info click'
    | 'subscription promo info click',
  product: Product,
  elementLabel?: {
    key: string;
    value: string;
  },
  subscription?: Subscription
) {
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: type,
    event_raised_by: RAISED_BY,
    ...(elementLabel
      ? { cta_name: `${elementLabel.key}: ${elementLabel.value} text clicked` }
      : {}),
    ...productToGtmProductPayloadEvent(product),
    ...(subscription
      ? subscriptionToGtmSubscriptionPayloadEvent(subscription)
      : {})
  });
}

// TODO: Prob makes no sense now
const buyOptionTypeToActionType: Record<string, (product: Product) => string> =
  {
    PROMOTIONS: (product) => (hasPromotions(product) ? 'sku promo' : ''),
    SUBSCRIPTIONS: (product) =>
      Object.keys(product.subscriptionsByDuration ?? {}).length !== 0
        ? 'subscription'
        : ''
  };

function hasPromotions(product?: Product) {
  return (product?.promotions ?? []).length !== 0;
}

export function trackRanges(title: string): void {
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: `ranges click: ${title.toLowerCase()}`,
    event_raised_by: RAISED_BY
  });
}

export const mediaGalleryImageViewCtaTypes = {
  default: 'default image',
  chevron: 'arrow scroll',
  thumbnail: 'thumbnail click'
} as const;

/** General views on image in the carousel */
export function trackMediaGalleryImageView(
  /** Note: The zero-based index of the image viewed */
  imageIndex: number,
  totalImages: number,
  /** Which user event triggered the viewing? Simply swiping the carousel, clicking the next/prev arrow or clicking carousel thumbnail */
  eventSource: keyof typeof mediaGalleryImageViewCtaTypes // don't import media gallery event source type here as to not couple
): void {
  const mappedEventSource =
    mediaGalleryImageViewCtaTypes[eventSource] ??
    mediaGalleryImageViewCtaTypes.default;
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: `image view - ${imageIndex + 1} out of ${totalImages}`, // Ex: carousel image - 1 out of 5
    cta_name: mappedEventSource,
    event_raised_by: RAISED_BY
  });
}

/** Tracking when the user enters the full screen gallery experience */
export function trackMediaGalleryFullScreenView(
  /** Note: The zero-based index of the image viewed */
  imageIndex: number,
  totalImages: number,
  /** Which user event triggered the viewing? Simply opening the full screen gallery, clicking the next/prev arrow or clicking thumbnail */
  eventSource: keyof typeof mediaGalleryImageViewCtaTypes // don't import media gallery event source type here as to not couple
): void {
  const mappedEventSource =
    mediaGalleryImageViewCtaTypes[eventSource] ??
    mediaGalleryImageViewCtaTypes.default;
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: `gallery view - ${imageIndex + 1} out of ${totalImages}`, // Ex: gallery image - 1 out of 5
    cta_name: mappedEventSource,
    event_raised_by: RAISED_BY
  });
}

export function trackMediaGalleryImageMagnify(
  /** Note: The zero-based index of the image viewed */
  imageIndex: number,
  totalImages: number
): void {
  // a.k.a. Zoom Image event
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: `zoom image - ${imageIndex + 1} out of ${totalImages}`, // Ex: zoom image - 1 out of 5
    event_raised_by: RAISED_BY
  });
}

export function trackRangeConfiguration(rangeTitle: string[]): void {
  const titleLength = rangeTitle?.length || 0;
  const titleString =
    rangeTitle && titleLength > 0
      ? rangeTitle.map((str: string) => str.toLocaleLowerCase()).join(' | ')
      : 'no ranges configured';

  const actionType = `ranges config - ${titleLength}: ${titleString}`;

  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: actionType,
    event_raised_by: RAISED_BY
  });
}

export function trackSubscriptionConfiguration(
  subscriptionsByDuration: Record<number, Subscription[]>
): void {
  const duration = getListOfSubscriptionsDurations(subscriptionsByDuration);
  const monthDuration =
    duration && duration?.length !== 0 ? duration.join(' | ') : '';

  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: 'subscription config',
    item_subscription_duration_event: `${monthDuration}`,
    event_raised_by: RAISED_BY
  });
}

export function trackSubscriptionsModalPlanList(
  product: Product,
  subscriptionsByDuration: Record<string, Subscription[]>,
  duration: number,
  currency?: string
): void {
  const durationString = `${duration}`;
  const subscriptions = subscriptionsByDuration[durationString] || [];

  // Map each subscription to an EcommerceItem
  const items: PlansItem[] = subscriptions.map((subscription, index) => ({
    ...productToGtmProductPayload(product, false, false),
    ...subscriptionToGtmSubscriptionPayload(subscription, product),
    ...subscriptionToGtmSubscriptionExtrasPayload(
      product,
      subscription,
      index + 1,
      duration
    )
  }));

  dispatchTrackingEvent({
    event: 'view_item_list',
    subscription_product_included: 'true',
    event_raised_by: RAISED_BY,
    ecommerce: {
      currency,
      value: product.pricing.price / PRICE_REPRESENTATION_MULTIPLIER,
      items
    }
  });
}

export type LastEventSource =
  | 'default image'
  | 'arrow scroll'
  | 'thumbnail click'
  | 'automatic';

export function trackVideo(
  videoId: string,
  ctaName: LastEventSource,
  actionType: string
) {
  dispatchTrackingEvent({
    event: 'pdp_interaction',
    action_type: actionType,
    click_location: videoId,
    cta_name: ctaName,
    event_raised_by: RAISED_BY
  });
}

export function trackVideoPlay(videoId: string, ctaName: LastEventSource) {
  trackVideo(videoId, ctaName, 'video play');
}

export function trackVideoPause(videoId: string, ctaName: LastEventSource) {
  trackVideo(videoId, ctaName, 'video pause');
}

export function trackVideoProgress(
  videoId: string,
  ctaName: LastEventSource,
  loop: number,
  percentage: number
) {
  trackVideo(
    videoId,
    ctaName,
    `video progress  - loop ${loop} - ${percentage}%`
  );
}

export function trackVideoComplete(videoId: string, ctaName: LastEventSource) {
  trackVideo(videoId, ctaName, 'video complete');
}
